/**
 * PuppetPositionControls Component (Task Group 4.2)
 *
 * Control panel buttons for puppet position management.
 * Located in the timeline controls bar next to the PuppetSelector.
 *
 * Features:
 * - "Reset to Start" button: instantly moves all body parts to starting positions
 * - "Dock Puppet" button: instantly moves all body parts to docking positions
 * - Buttons are disabled when no puppet is active
 * - Uses Lucide icons for visual clarity
 * - Follows button styling from TimelineControls.tsx
 */

import { RotateCcw, Anchor } from 'lucide-react';
import { usePuppetStore } from '@/stores/puppetStore';

export function PuppetPositionControls() {
  // Get puppet state and actions from store
  const activePuppet = usePuppetStore((state) => state.activePuppet);
  const resetToStartPosition = usePuppetStore(
    (state) => state.resetToStartPosition
  );
  const dockPuppet = usePuppetStore((state) => state.dockPuppet);

  // Determine if buttons should be disabled
  const isDisabled = !activePuppet;

  /**
   * Handle Reset to Start button click
   * Triggers store action to set pendingPositionAction = 'reset'
   */
  const handleResetToStart = () => {
    resetToStartPosition();
  };

  /**
   * Handle Dock Puppet button click
   * Triggers store action to set pendingPositionAction = 'dock'
   */
  const handleDockPuppet = () => {
    dockPuppet();
  };

  return (
    <div
      className="flex items-center gap-2"
      data-testid="puppet-position-controls"
    >
      {/* Reset to Start button */}
      <button
        onClick={handleResetToStart}
        disabled={isDisabled}
        className="flex items-center justify-center w-10 h-10 rounded bg-[#1e1e1e] hover:bg-[#2a2a2a] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#1e1e1e]"
        aria-label="Reset to Start"
        title="Reset puppet to starting position"
        data-testid="reset-to-start-button"
      >
        <RotateCcw className="w-5 h-5 text-white" />
      </button>

      {/* Dock Puppet button */}
      <button
        onClick={handleDockPuppet}
        disabled={isDisabled}
        className="flex items-center justify-center w-10 h-10 rounded bg-[#1e1e1e] hover:bg-[#2a2a2a] transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#1e1e1e]"
        aria-label="Dock Puppet"
        title="Move puppet to docking position"
        data-testid="dock-puppet-button"
      >
        <Anchor className="w-5 h-5 text-white" />
      </button>
    </div>
  );
}
